import CloseIcon from '@mui/icons-material/Close';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import {
    alpha,
    Avatar,
    Box,
    Button,
    Chip,
    Dialog,
    Grid,
    IconButton,
    LinearProgress,
    Menu,
    MenuItem,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
    useTheme
} from '@mui/material';
import React, { useState } from 'react';
import { bytesToMB, getProgressPercent } from '../../utils/helper';
import api from '../../utilities/api';

const UserProfileModal = ({ open, onClose, user }) => {
    const theme = useTheme(); // Get the current theme

    // State for handling the action menu
    const [actionAnchorEl, setActionAnchorEl] = useState(null);
    const [currentSubscriptionId, setCurrentSubscriptionId] = useState(null);

    // Handle opening the action menu
    const handleActionMenuOpen = (event, subscriptionId) => {
        setActionAnchorEl(event.currentTarget);
        setCurrentSubscriptionId(subscriptionId);
    };

    // Handle closing the action menu
    const handleActionMenuClose = () => {
        setActionAnchorEl(null);
        setCurrentSubscriptionId(null);
    };

    // Handle selecting an action from the menu
    const handleActionSelect = (action) => {
        // Here you would implement the action functionality
        console.log(`Action ${action} selected for subscription ${currentSubscriptionId}`);
        handleActionMenuClose();
    };

    if (!user) return null;

    // Mock subscriptions data with icon images instead of emoji icons
    const subscriptions = [
        {
            id: 2,
            name: 'Clikkle Chat',
            iconImage: 'https://cdn.clikkle.com/images/chat/logo/2025/clikkle%20chat.png',
            storageUsed: '0.95 GB Used',
            status: 'Active',
            action: 'Activate'
        },
        {
            id: 3,
            name: 'Clikkle Campaigns',
            iconImage: 'https://cdn.clikkle.com/images/campaigns/logo/2025/clikkle%20campaigns.png',
            storageUsed: '3.33 GB Used',
            status: 'Active',
            action: 'Deactivate'
        },
        {
            id: 4,
            name: 'Clikkle eSign',
            iconImage: 'https://cdn.clikkle.com/images/e-sign/logo/2025/clikkle%20esign.png',
            storageUsed: '784 KB Used',
            status: 'Active',
            action: 'Overdue'
        },
        {
            id: 5,
            name: 'Clikkle Files',
            iconImage: 'https://cdn.clikkle.com/images/files/logo/2025/clikkle%20files.png',
            storageUsed: '2.40 GB Used',
            status: 'Active'
        },
        {
            id: 6,
            name: 'Clikkle Projects',
            iconImage: 'https://cdn.clikkle.com/images/projects/logo/2025/clikkle%20projects.png',
            storageUsed: '2.40 GB Used',
            status: 'Active'
        },
        {
            id: 7,
            name: 'Clikkle Hr',
            iconImage: 'https://cdn.clikkle.com/images/hr/logo/2025/clikkle%20hr.png',
            storageUsed: '2.40 GB Used',
            status: 'Active'
        },
        {
            id: 8,
            name: 'Clikkle Worksuite',
            iconImage: 'https://cdn.clikkle.com/images/worksuite/2025/clikkle%20worksuite.png',
            storageUsed: '2.40 GB Used',
            status: 'Active'
        }
    ];

    // Account actions
    const accountActions = [
        { name: 'Suspend', color: 'warning' },
        { name: 'Delete', color: 'error' },
        { name: 'Send Verification Email', color: 'primary' }
    ];

    // helper to safely format dates
    const fmtDate = (d) => {
        try {
            if (!d) return '-';
            const date = new Date(d);
            if (isNaN(date)) return String(d);
            return date.toLocaleString();
        } catch (e) {
            return String(d);
        }
    };

    async function deleteUser() {
    try {
        if (!user?._id) {
        console.error("No user id provided");
        return;
        }
        console.log("Deleting user with id:", user._id);
        const response = await api.delete(`/api/user/users/${user._id}`);
        console.log("User deleted successfully:", response.data);
        window.location.reload()
        onClose()
    } catch (error) {
        console.error("Error deleting user:", error.response?.data || error.message);
    }
    }

    async function sendVerificationEmail() {
        try {
            if (!user?._id) {
                console.error("No user id provided");
                return;
            }
            console.log("Sending verification email to user with id:", user._id);
            const response = await api.post(`/api/user/users/${user._id}/send-verification-email`);
            console.log("Verification email sent successfully:", response.data);
            // You can add a success notification here if needed
        } catch (error) {
            console.error("Error sending verification email:", error.response?.data || error.message);
            // You can add an error notification here if needed
        }
    }

    async function suspendUser() {
        try {
            if (!user?._id) {
                console.error("No user id provided");
                return;
            }
            console.log("Suspended user with id:", user._id);
            const response = await api.patch(`/api/user/users/${user._id}/suspend`);
            console.log("User Suspended successfully:", response.data);
            onClose()
        } catch (error) {
            console.error("Error Suspended user:", error.response?.data || error.message);
        }
    }

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="lg"
            PaperProps={{
                sx: {
                    bgcolor: theme.palette.background.default,
                    color: theme.palette.text.primary,
                    borderRadius: 3,
                    boxShadow: 24,
                    p: { xs: 2, sm: 3 },
                    maxHeight: '90vh',
                }
            }}
        >
            <Box sx={{ position: 'relative', overflowY: 'auto' }}>
                <Typography variant="h4" fontWeight="bold">
                    User Profile
                </Typography>
                <IconButton
                    aria-label="close"
                    onClick={onClose}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: theme.palette.text.primary,
                    }}
                >
                    <CloseIcon />
                </IconButton>

                {/* User Header Section */}
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 2, justifyContent: 'center', flexDirection: { xs: 'column', sm: 'row' } }}>
                    {user.profilePicture ? (
                        <Box
                            component="img"
                            src={user.profilePicture}
                            alt="Profile"
                            sx={{
                                width: 40,
                                height: 40,
                                borderRadius: '50%',
                                objectFit: 'cover',
                            }}
                        />
                    ) : (
                        <Avatar
                            sx={{
                                width: 40,
                                height: 40,
                                bgcolor: theme.palette.primary.main,
                                color: theme.palette.primary.contrastText,
                            }}
                        >
                            {(user?.firstName?.[0] || '').toUpperCase()}
                            {(user?.lastName?.[0] || '').toUpperCase()}
                        </Avatar>
                    )}
                    <Box sx={{ ml: { xs: 0, sm: 2 }, textAlign: { xs: 'center', sm: 'left' }, mt: { xs: 1, sm: 0 } }}>
                        <Typography variant="h5" fontWeight="bold">{user.firstName} {user.lastName}</Typography>
                        <Chip
                            label={ user.isVerified === true ? 'Verified' : "Unverified"}
                            size="small"
                            sx={{
                                bgcolor: user.isVerified === true ? theme.palette.success.main : theme.palette.grey[500],
                                color: theme.palette.common.white,
                                '& .MuiChip-label': { px: 1.5 }
                            }}
                        />
                    </Box>
                </Box>

                {/* User Stats */}
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, gap: { xs: "20px", sm: "40px", md: "80px" }, flexWrap: 'wrap' }}>
                    <Box sx={{ textAlign: 'center', minWidth: { xs: '25%', sm: 'auto' } }}>
                        <Typography variant="h6">{fmtDate(user?.createdAt)}</Typography>
                        <Typography variant="body2" color="text.secondary">Registration Date</Typography>
                    </Box>
                    <Box sx={{ textAlign: 'center', minWidth: { xs: '25%', sm: 'auto' } }}>
                        <Typography variant="h6">7</Typography>
                        <Typography variant="body2" color="text.secondary">Subscription</Typography>
                    </Box>
                    <Box sx={{ textAlign: 'center', minWidth: { xs: '25%', sm: 'auto' } }}>
                        <Typography variant="h6" color="warning.main">
                            {bytesToMB(user?.storage?.usedSpace)} / {user?.storagePermission?.noOfAllowed || '0'} {(user?.storagePermission?.format || 'MB')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">Storage</Typography>
                    </Box>
                </Box>

                <Grid container spacing={{ xs: 1, sm: 2, md: 3 }} sx={{ mt: 11 }}>
                    {/* User Details and Storage in the same row */}
                    <Grid item xs={12} sm={10} md={10}>
                        <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>User Details</Typography>
                        <Grid container spacing={{ xs: 1, sm: 2 }}>
                            <Grid item xs={12} sm={6} md={4}>
                                <Typography variant="body2" color="text.secondary">Email:</Typography>
                                <Typography variant="body1" color="primary" sx={{ wordBreak: 'break-word' }}>
                                    {user.email}
                                </Typography>
                            </Grid>

                            {/* existing fields preserved */}
                            <Grid item xs={12} sm={6} md={4}>
                                <Typography variant="body2" color="text.secondary">Phone Number:</Typography>
                                <Typography variant="body1"> {user?.phoneNumber || 'Not Provided'}</Typography>
                            </Grid>

                            <Grid item xs={12} sm={6} md={4}>
                                <Typography variant="body2" color="text.secondary">IP Address:</Typography>
                                <Typography variant="body1">{user?.registerIpAddress
  ? user.registerIpAddress === '::1'
    ? 'DEVELOPMENT_MODE'
    : user.registerIpAddress.replace(/^::ffff:/, '')
  : 'Not Provided'}</Typography>
                            </Grid>

                            <Grid item xs={12} sm={6} md={4}>
                                <Typography variant="body2" color="text.secondary">Date of Birth:</Typography>
                                <Typography variant="body1">{user?.dob || 'Not Provided'}</Typography>
                            </Grid>

                            <Grid item xs={12} sm={6} md={4}>
                                <Typography variant="body2" color="text.secondary">Recovery Email:</Typography>
                                <Typography variant="body1">{user?.recoveryEmail || 'Not Provided'}</Typography>
                            </Grid>

                            <Grid item xs={12} sm={6} md={4}>
                                <Typography variant="body2" color="text.secondary">Session Active:</Typography>
                                <Typography variant="body1">{user?.session ? 'Yes' : 'No'}</Typography>
                            </Grid>

                            <Grid item xs={12} sm={6} md={4}>
                                <Typography variant="body2" color="text.secondary">Account Type:</Typography>
                                <Typography variant="body1">{user?.accountType || 'Not Provided'}</Typography>
                            </Grid>

                            <Grid item xs={12} sm={6} md={4}>
                                <Typography variant="body2" color="text.secondary">Role:</Typography>
                                <Typography variant="body1">{user?.role || 'Not Provided'}</Typography>
                            </Grid>

                            <Grid item xs={12} sm={6} md={4}>
                                <Typography variant="body2" color="text.secondary">Referred By:</Typography>
                                <Typography variant="body1">{user?.referred_by || 'Not Provided'}</Typography>
                            </Grid>

                            <Grid item xs={12} sm={6} md={4}>
                                <Typography variant="body2" color="text.secondary">Firebase UID:</Typography>
                                <Typography variant="body1">{user?.firebaseUid || 'Not Provided'}</Typography>
                            </Grid>

                            <Grid item xs={12} sm={6} md={4}>
                                <Typography variant="body2" color="text.secondary">Auth Providers:</Typography>
                                <Typography variant="body1">{Array.isArray(user?.authProvider) ? user.authProvider.join(', ') : (user?.authProvider || 'Auth')}</Typography>
                            </Grid>

                            <Grid item xs={12} sm={6} md={4}>
                                <Typography variant="body2" color="text.secondary">Provider IDs:</Typography>
                                <Typography variant="body1" sx={{ wordBreak: 'break-word' }}>{user?.providerIds ? JSON.stringify(user.providerIds) : 'Not Provided'}</Typography>
                            </Grid>

                            <Grid item xs={12} sm={6} md={4}>
                                <Typography variant="body2" color="text.secondary">Username:</Typography>
                                <Typography variant="body1">{user?.username || 'Not Provided'}</Typography>
                            </Grid>

                            <Grid item xs={12} sm={6} md={4}>
                                <Typography variant="body2" color="text.secondary">Gender:</Typography>
                                <Typography variant="body1">{user?.gender || 'Not Provided'}</Typography>
                            </Grid>

                            <Grid item xs={12} sm={6} md={4}>
                                <Typography variant="body2" color="text.secondary">Created At:</Typography>
                                <Typography variant="body1">{fmtDate(user?.createdAt)}</Typography>
                            </Grid>

                            <Grid item xs={12} sm={6} md={4}>
                                <Typography variant="body2" color="text.secondary">Updated At:</Typography>
                                <Typography variant="body1">{fmtDate(user?.updatedAt)}</Typography>
                            </Grid>

                            <Grid item xs={12} sm={6} md={4}>
                                <Typography variant="body2" color="text.secondary">Is Verified:</Typography>
                                <Typography variant="body1">{user?.isVerified ? 'Yes' : 'No'}</Typography>
                            </Grid>

                            <Grid item xs={12} sm={6} md={4}>
                                <Typography variant="body2" color="text.secondary">Profile Picture (URL):</Typography>
                                <Typography variant="body1" sx={{ wordBreak: 'break-word' }}>{user?.profilePicture || '-'}</Typography>
                            </Grid>

                            <Grid item xs={12} sm={6} md={4}>
                                <Typography variant="body2" color="text.secondary">Reset Password Token:</Typography>
                                <Typography variant="body1" sx={{ wordBreak: 'break-word' }}>{user?.resetPasswordToken ? `${String(user.resetPasswordToken).slice(0, 20)}...` : '-'}</Typography>
                            </Grid>

                            <Grid item xs={12} sm={6} md={4}>
                                <Typography variant="body2" color="text.secondary">Reset Password Expire:</Typography>
                                <Typography variant="body1">{fmtDate(user?.resetPasswordExpire)}</Typography>
                            </Grid>

                            {/* Business details expanded */}
                            <Grid item xs={12}>
                                <Typography variant="body2" color="text.secondary">Business Details:</Typography>
                                {user?.business ? (
                                    <Box sx={{ mt: 0.5, pl: 1 }}>
                                        <Typography variant="body2">Business Name: {user.business.businessName || 'Not Provided'}</Typography>
                                        <Typography variant="body2">Domain: {user.business.domain || 'Not Provided'}</Typography>
                                        <Typography variant="body2">Employees: {user.business.numberOfEmployees || 'Not Provided'}</Typography>
                                        <Typography variant="body2">Phone: {user.business.businessPhoneNumber || 'Not Provided'}</Typography>
                                        <Typography variant="body2">Street: {user.business.streetAddress || 'Not Provided'}</Typography>
                                        <Typography variant="body2">Street Line 2: {user.business.streetAddressLine2 || 'Not Provided'}</Typography>
                                        <Typography variant="body2">City: {user.business.city || 'Not Provided'}</Typography>
                                        <Typography variant="body2">LGA: {user.business.LocalGovernmentArea || 'Not Provided'}</Typography>
                                        <Typography variant="body2">Postal Code: {user.business.postalCode || 'Not Provided'}</Typography>
                                        <Typography variant="body2">State: {user.business.state || 'Not Provided'}</Typography>
                                    </Box>
                                ) : (
                                    <Typography variant="body1">Not Provided</Typography>
                                )}
                            </Grid>

                        </Grid>
                    </Grid>

                    {/* Subscriptions Section */}
                    <Grid item xs={12}>
                        <Typography variant="h6" fontWeight="bold" sx={{ mb: 1, mt: 1 }}>Subscriptions</Typography>
                        <Box sx={{ overflow: 'auto' }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Product</TableCell>
                                        <TableCell>Storage Used</TableCell>
                                        <TableCell>Action</TableCell>
                                        <TableCell>Status</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {subscriptions.map((subscription) => (
                                        <TableRow key={subscription.id}>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                    <Box
                                                        component="img"
                                                        src={subscription.iconImage}
                                                        alt={subscription.name}
                                                        sx={{
                                                            width: 24,
                                                            height: 24,
                                                            mr: 1,
                                                            objectFit: 'contain'
                                                        }}
                                                    />
                                                    <Typography variant="body2">{subscription.name}</Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>{subscription.storageUsed}</TableCell>
                                            <TableCell>
                                                <IconButton
                                                    size="small"
                                                    onClick={(e) => handleActionMenuOpen(e, subscription.id)}
                                                    sx={{
                                                        padding: '4px',
                                                        color: theme.palette.text.secondary
                                                    }}
                                                >
                                                    <MoreVertIcon fontSize="small" />
                                                </IconButton>
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={subscription.status}
                                                    size="small"
                                                    sx={{
                                                        bgcolor: subscription.status === 'Active' ? theme.palette.success.main : theme.palette.grey[500],
                                                        color: theme.palette.getContrastText(
                                                            subscription.status === 'Active' ? theme.palette.success.main : theme.palette.grey[500]
                                                        ),
                                                        fontSize: '0.7rem',
                                                        height: 24
                                                    }}
                                                />
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Box>

                        {/* Action Menu */}
                        <Menu
                            anchorEl={actionAnchorEl}
                            open={Boolean(actionAnchorEl)}
                            onClose={handleActionMenuClose}
                            PaperProps={{
                                sx: {
                                    bgcolor: theme.palette.mode === 'dark' ? theme.palette.grey[900] : theme.palette.grey[800],
                                    color: theme.palette.common.white,
                                    borderRadius: '8px',
                                    boxShadow: '0 2px 10px rgba(0,0,0,0.3)',
                                    width: '150px'
                                }
                            }}
                        >
                            <MenuItem
                                onClick={() => handleActionSelect('Activate')}
                                sx={{
                                    py: 1,
                                    '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' }
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Box
                                        component="span"
                                        sx={{
                                            width: 18,
                                            height: 18,
                                            borderRadius: '50%',
                                            border: `2px solid ${theme.palette.success.main}`,
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}
                                    >
                                        <Box
                                            component="span"
                                            sx={{
                                                width: 8,
                                                height: 8,
                                                bgcolor: theme.palette.success.main,
                                                borderRadius: '50%'
                                            }}
                                        />
                                    </Box>
                                    <Typography variant="body2">Activate</Typography>
                                </Box>
                            </MenuItem>
                            <MenuItem
                                onClick={() => handleActionSelect('Deactivate')}
                                sx={{
                                    py: 1,
                                    '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' }
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Box
                                        component="span"
                                        sx={{
                                            width: 18,
                                            height: 18,
                                            borderRadius: '50%',
                                            border: `2px solid ${theme.palette.error.main}`,
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}
                                    >
                                        <Box
                                            component="span"
                                            sx={{
                                                width: 8,
                                                height: 8,
                                                bgcolor: theme.palette.error.main,
                                                borderRadius: '50%'
                                            }}
                                        />
                                    </Box>
                                    <Typography variant="body2">Deactivate</Typography>
                                </Box>
                            </MenuItem>
                            <MenuItem
                                onClick={() => handleActionSelect('Overdue')}
                                sx={{
                                    py: 1,
                                    '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' }
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Box
                                        component="span"
                                        sx={{
                                            width: 18,
                                            height: 18,
                                            borderRadius: '50%',
                                            border: `2px solid ${theme.palette.warning.main}`,
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}
                                    >
                                        <Box
                                            component="span"
                                            sx={{
                                                width: 8,
                                                height: 8,
                                                bgcolor: theme.palette.warning.main,
                                                borderRadius: '50%'
                                            }}
                                        />
                                    </Box>
                                    <Typography variant="body2">Overdue</Typography>
                                </Box>
                            </MenuItem>
                        </Menu>
                    </Grid>

                    {/* Account Action */}
                    <Grid item xs={12} md={6}>
                        <Typography variant="h6" fontWeight="bold" sx={{ mb: 1, mt: 1 }}>Account Action</Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {accountActions.map((action) => (
                                <Button
                                    key={action.name}
                                    variant="contained"
                                    color={action.color}
                                    size="small"
                                    onClick={
                                        action.name === 'Delete' ? deleteUser : 
                                        action.name === 'Suspend' ? suspendUser : 
                                        action.name === 'Send Verification Email' ? sendVerificationEmail : 
                                        null
                                    }
                                    sx={{
                                        borderRadius: 1,
                                        minWidth: { xs: 80, sm: 100 },
                                        fontSize: { xs: '0.75rem', sm: '0.875rem' }
                                    }}
                                >
                                    {action.name}
                                </Button>
                            ))}
                        </Box>
                    </Grid>
                </Grid>
            </Box>
        </Dialog>
    );
};

export default UserProfileModal;
